// PATH: src/components/catalog/catalogModel.ts
/**
 * Catalogue : règles pures (aucun appel réseau, aucun React). Une seule
 * source : la réponse de `GET /v1/curriculum/overview`, indexée ici pour
 * l'arbre, les panneaux de détail et les formulaires.
 */
import type {
  CurriculumGrade,
  CurriculumProgram,
  CurriculumResponse,
  CurriculumStage,
  Subject,
  Track,
} from "../../models/domain";

// ---------------------------------------------------------------------------
// Nœud sélectionné (dans l'URL : ?node=program:<id> | stage:<id> | grade:<id>)
// ---------------------------------------------------------------------------

export type NodeKind = "program" | "stage" | "grade";
export interface NodeRef {
  kind: NodeKind;
  id: string;
}

const KINDS: NodeKind[] = ["program", "stage", "grade"];

export const parseNodeParam = (raw: string | null | undefined): NodeRef | null => {
  if (!raw) return null;
  const i = raw.indexOf(":");
  if (i <= 0) return null;
  const kind = raw.slice(0, i) as NodeKind;
  const id = raw.slice(i + 1).trim();
  if (!KINDS.includes(kind) || !id) return null;
  return { kind, id };
};

export const formatNodeParam = (node: NodeRef): string => `${node.kind}:${node.id}`;

export const sameNode = (a: NodeRef | null, b: NodeRef | null): boolean =>
  Boolean(a && b && a.kind === b.kind && a.id === b.id);

// ---------------------------------------------------------------------------
// Index
// ---------------------------------------------------------------------------

export interface StageEntry {
  program: CurriculumProgram;
  stage: CurriculumStage;
}
export interface GradeEntry extends StageEntry {
  grade: CurriculumGrade;
}

export interface CatalogIndex {
  programs: CurriculumProgram[];
  programById: Map<string, CurriculumProgram>;
  stageById: Map<string, StageEntry>;
  gradeById: Map<string, GradeEntry>;
  /** Niveaux où chaque matière est proposée (id matière → niveaux). */
  gradesBySubject: Map<string, GradeEntry[]>;
}

export const indexCatalog = (data: CurriculumResponse | null | undefined): CatalogIndex => {
  const programs = data?.programs ?? [];
  const programById = new Map<string, CurriculumProgram>();
  const stageById = new Map<string, StageEntry>();
  const gradeById = new Map<string, GradeEntry>();
  const gradesBySubject = new Map<string, GradeEntry[]>();
  for (const program of programs) {
    programById.set(program.program.id, program);
    for (const stage of program.stages ?? []) {
      stageById.set(stage.stage.id, { program, stage });
      for (const grade of stage.grades ?? []) {
        const entry = { program, stage, grade };
        gradeById.set(grade.grade.id, entry);
        for (const o of grade.offerings ?? []) {
          const list = gradesBySubject.get(o.offering.subject_id) ?? [];
          list.push(entry);
          gradesBySubject.set(o.offering.subject_id, list);
        }
      }
    }
  }
  return { programs, programById, stageById, gradeById, gradesBySubject };
};

/** Le nœud existe-t-il encore (après une suppression, un lien périmé) ? */
export const nodeExists = (index: CatalogIndex, node: NodeRef | null): boolean => {
  if (!node) return false;
  if (node.kind === "program") return index.programById.has(node.id);
  if (node.kind === "stage") return index.stageById.has(node.id);
  return index.gradeById.has(node.id);
};

/** Ancêtres d'un nœud, du programme au nœud lui-même. */
export const nodePath = (index: CatalogIndex, node: NodeRef | null): NodeRef[] => {
  if (!node) return [];
  if (node.kind === "program") return index.programById.has(node.id) ? [node] : [];
  if (node.kind === "stage") {
    const e = index.stageById.get(node.id);
    return e ? [{ kind: "program", id: e.program.program.id }, node] : [];
  }
  const g = index.gradeById.get(node.id);
  return g
    ? [{ kind: "program", id: g.program.program.id }, { kind: "stage", id: g.stage.stage.id }, node]
    : [];
};

/**
 * Nœud à afficher : celui demandé s'il existe ; sinon son parent encore
 * présent (lien vers un niveau supprimé → son étape) ; sinon le 1er programme.
 */
export const resolveNode = (
  index: CatalogIndex,
  wanted: NodeRef | null,
  fallbackParent?: NodeRef | null,
): NodeRef | null => {
  if (wanted && nodeExists(index, wanted)) return wanted;
  if (fallbackParent && nodeExists(index, fallbackParent)) return fallbackParent;
  const first = index.programs[0];
  return first ? { kind: "program", id: first.program.id } : null;
};

// ---------------------------------------------------------------------------
// Compteurs
// ---------------------------------------------------------------------------

export interface ProgramCounts {
  stages: number;
  grades: number;
  subjects: number;
  offerings: number;
  tracks: number;
}

export const programCounts = (p: CurriculumProgram): ProgramCounts => {
  let grades = 0;
  let offerings = 0;
  let tracks = 0;
  for (const s of p.stages ?? []) {
    const c = stageCounts(s);
    grades += c.grades;
    offerings += c.offerings;
    tracks += c.tracks;
  }
  return { stages: (p.stages ?? []).length, grades, subjects: (p.subjects ?? []).length, offerings, tracks };
};

export const stageCounts = (s: CurriculumStage) => {
  let offerings = 0;
  let tracks = 0;
  for (const g of s.grades ?? []) {
    offerings += (g.offerings ?? []).length;
    tracks += (g.tracks ?? []).length;
  }
  return { grades: (s.grades ?? []).length, offerings, tracks };
};

/** « 1 niveau », « 3 niveaux », « aucun niveau ». */
export const plural = (n: number, one: string, many?: string, none?: string): string => {
  if (n === 0 && none) return none;
  return `${n} ${n > 1 ? many ?? `${one}s` : one}`;
};

// ---------------------------------------------------------------------------
// Recherche dans l'arbre
// ---------------------------------------------------------------------------

/** Minuscules sans accents : « Collège » trouve « college ». */
export const normalizeText = (s: string | null | undefined): string =>
  (s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

const hit = (q: string, ...fields: Array<string | null | undefined>) =>
  fields.some((f) => normalizeText(f).includes(q));

export interface TreeFilter {
  /** Ids visibles (programmes, étapes, niveaux). `null` = pas de filtre. */
  visible: Set<string> | null;
  /** Ids à déplier pour montrer les correspondances. */
  expand: Set<string>;
}

/**
 * Filtre de l'arbre : un nœud est visible s'il correspond, si un descendant
 * correspond (on montre le chemin) ou si un ancêtre correspond (on montre
 * tout son contenu). Recherche sur le nom et le code.
 */
export const filterTree = (programs: CurriculumProgram[], query: string): TreeFilter => {
  const q = normalizeText(query);
  if (!q) return { visible: null, expand: new Set() };
  const visible = new Set<string>();
  const expand = new Set<string>();
  for (const p of programs) {
    const pid = p.program.id;
    const pHit = hit(q, p.program.name, p.program.code);
    for (const s of p.stages ?? []) {
      const sid = s.stage.id;
      const sHit = pHit || hit(q, s.stage.name, s.stage.code);
      for (const g of s.grades ?? []) {
        const gHit = sHit || hit(q, g.grade.name, g.grade.code);
        if (gHit) {
          visible.add(g.grade.id);
          visible.add(sid);
          visible.add(pid);
          if (!sHit) {
            expand.add(sid);
            expand.add(pid);
          }
        }
      }
      if (sHit) {
        visible.add(sid);
        visible.add(pid);
        if (!pHit) expand.add(pid);
      }
    }
    if (pHit) visible.add(pid);
  }
  return { visible, expand };
};

// ---------------------------------------------------------------------------
// Formulaires : ordre, doublons, nombres
// ---------------------------------------------------------------------------

/** Ordre proposé pour un nouvel élément : le plus grand + 1 (1 si vide). */
export const nextSortOrder = (items: Array<{ sort_order?: number | null }>): number =>
  items.reduce((m, x) => Math.max(m, x.sort_order ?? 0), 0) + 1;

const sameCode = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Un frère porte-t-il déjà ce code (insensible à la casse, comme la base) ? */
export const codeTaken = (
  siblings: Array<{ id: string; code: string }>,
  code: string,
  selfId?: string | null,
): boolean => Boolean(code.trim()) && siblings.some((s) => s.id !== selfId && sameCode(s.code, code));

/** Un frère occupe-t-il déjà ce rang ? (étapes et matières : unique en base). */
export const orderTaken = (
  siblings: Array<{ id: string; sort_order?: number | null }>,
  order: number,
  selfId?: string | null,
): boolean => siblings.some((s) => s.id !== selfId && (s.sort_order ?? 0) === order);

/** Entier saisi : `null` si vide, `NaN` si invalide. */
export const parseOptionalInt = (raw: string): number | null => {
  const t = raw.trim();
  if (!t) return null;
  return /^-?\d+$/.test(t) ? Number(t) : NaN;
};

/** Décimal saisi (virgule acceptée) : `null` si vide, `NaN` si invalide. */
export const parseOptionalNumber = (raw: string): number | null => {
  const t = raw.trim().replace(",", ".");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};

/** Couleur « #rgb » ou « #rrggbb » ; vide = pas de couleur. */
export const isHexColor = (s: string): boolean => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s.trim());

/** Chaîne vide → null (champs optionnels envoyés au serveur). */
export const emptyToNull = (s: string | null | undefined): string | null => {
  const t = (s ?? "").trim();
  return t ? t : null;
};

// ---------------------------------------------------------------------------
// Matières et offres
// ---------------------------------------------------------------------------

/** Couleur d'une matière pour sa pastille (couleur dédiée, sinon primaire). */
export const subjectColor = (s: Pick<Subject, "color_hex" | "color_primary"> | null | undefined): string | null =>
  s?.color_hex || s?.color_primary || null;

/** Matières du programme pas encore proposées à ce niveau (+ celle de l'offre éditée). */
export const offerableSubjects = (
  subjects: Subject[],
  grade: CurriculumGrade,
  keepSubjectId?: string | null,
): Subject[] => {
  const taken = new Set((grade.offerings ?? []).map((o) => o.offering.subject_id));
  return subjects.filter((s) => s.id === keepSubjectId || !taken.has(s.id));
};

/** Tri des filières : rang puis nom. */
export const sortTracks = (tracks: Track[] | null | undefined): Track[] =>
  [...(tracks ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name));

/**
 * Filières d'une offre, dans l'ordre du niveau. Vide = toutes les filières.
 * Une filière inconnue (supprimée) est ignorée.
 */
export const offeringTracks = (trackIds: string[] | null | undefined, tracks: Track[] | null | undefined): Track[] => {
  const ids = new Set(trackIds ?? []);
  return sortTracks(tracks).filter((t) => ids.has(t.id));
};

/** Une offre vaut-elle pour cette filière ? (aucune filière = toutes) */
export const offeringAppliesToTrack = (offering: { track_ids?: string[] | null }, trackId: string): boolean => {
  const ids = offering.track_ids ?? [];
  return ids.length === 0 || ids.includes(trackId);
};

/** Les filières choisies ont-elles changé (ordre indifférent) ? */
export const sameIdSet = (a: string[] | null | undefined, b: string[] | null | undefined): boolean => {
  const x = new Set(a ?? []);
  const y = new Set(b ?? []);
  if (x.size !== y.size) return false;
  for (const id of Array.from(x)) if (!y.has(id)) return false;
  return true;
};

/**
 * Offres qui ne citent QUE cette filière : en la supprimant, leur liste
 * devient vide, ce qui veut dire « toutes les filières » (contrat API).
 */
export const offeringsOnlyForTrack = (grade: CurriculumGrade, trackId: string) =>
  (grade.offerings ?? []).filter((o) => {
    const ids = o.offering.track_ids ?? [];
    return ids.length === 1 && ids[0] === trackId;
  });

// ---------------------------------------------------------------------------
// Erreurs serveur → message lisible
// ---------------------------------------------------------------------------

export type EntityKind = "program" | "stage" | "grade" | "subject" | "track" | "offering";

const WHERE: Record<EntityKind, string> = {
  program: "un autre programme",
  stage: "une autre étape de ce programme",
  grade: "un autre niveau de cette étape",
  subject: "une autre matière de ce programme",
  track: "une autre filière de ce niveau",
  offering: "une autre offre",
};

/**
 * Message d'une erreur d'écriture. Le serveur répond 409 `{error:"conflict",
 * constraint}` sur une violation d'unicité, 422 `{error, field}` sur une
 * validation, 500 avec le texte Postgres sur une clé étrangère.
 */
export const writeErrorMessage = (e: unknown, entity: EntityKind): string => {
  const err = e as {
    response?: { status?: number; data?: { error?: string; message?: string; constraint?: string } };
    message?: string;
  };
  const status = err?.response?.status;
  const data = err?.response?.data ?? {};
  const constraint = (data.constraint ?? "").toLowerCase();
  if (status === 409) {
    if (constraint.includes("sort_order")) return `Ce rang est déjà occupé par ${WHERE[entity]}. Choisissez-en un autre.`;
    if (constraint.includes("subject_id_grade_id") || (entity === "offering" && constraint))
      return "Cette matière est déjà proposée à ce niveau.";
    if (constraint.includes("code")) return `Ce code est déjà utilisé par ${WHERE[entity]}.`;
    if (entity === "track") return "Des chapitres sont rattachés à cette filière : rattachez-les ailleurs ou désactivez-la plutôt.";
    return "Conflit avec une donnée existante.";
  }
  if (status === 405) return "Écriture refusée par le serveur (environnement en lecture seule ?).";
  const raw = String(data.error || data.message || err?.message || "");
  if (/foreign key|violates/i.test(raw)) return "Des chapitres importés dépendent encore de cet élément.";
  if (status === 422 && data.error) return String(data.error);
  return raw || "Une erreur est survenue.";
};

// ---------------------------------------------------------------------------
// Suppression : ce qui part avec (cascade réelle de la base)
// ---------------------------------------------------------------------------

/**
 * Ce que la base supprime avec l'élément (ON DELETE CASCADE) :
 * programme → étapes → niveaux → filières, offres, Mission Zéro ; matières ;
 * ossature prévue. Les chapitres importés ne sont PAS en cascade (le
 * programme les bloque, les autres les laisseraient orphelins) : l'écran les
 * supprime d'abord, et leur nombre est ajouté par l'appelant.
 */
export const cascadeLines = (
  index: CatalogIndex,
  target:
    | { kind: "program"; id: string }
    | { kind: "stage"; id: string }
    | { kind: "grade"; id: string }
    | { kind: "subject"; programId: string; id: string },
): string[] => {
  const lines: string[] = [];
  const nz = (n: number, one: string, many?: string) => {
    if (n > 0) lines.push(plural(n, one, many));
  };
  if (target.kind === "program") {
    const p = index.programById.get(target.id);
    if (!p) return lines;
    const c = programCounts(p);
    nz(c.stages, "étape");
    nz(c.grades, "niveau", "niveaux");
    nz(c.tracks, "filière");
    nz(c.subjects, "matière");
    nz(c.offerings, "offre de matière", "offres de matières");
    lines.push("l'ossature prévue et les configurations Mission Zéro du programme");
  } else if (target.kind === "stage") {
    const e = index.stageById.get(target.id);
    if (!e) return lines;
    const c = stageCounts(e.stage);
    nz(c.grades, "niveau", "niveaux");
    nz(c.tracks, "filière");
    nz(c.offerings, "offre de matière", "offres de matières");
    if (c.grades > 0) lines.push("l'ossature prévue et les configurations Mission Zéro de ces niveaux");
  } else if (target.kind === "grade") {
    const e = index.gradeById.get(target.id);
    if (!e) return lines;
    nz((e.grade.tracks ?? []).length, "filière");
    nz((e.grade.offerings ?? []).length, "offre de matière", "offres de matières");
    lines.push("l'ossature prévue et la configuration Mission Zéro du niveau");
  } else {
    const n = (index.gradesBySubject.get(target.id) ?? []).length;
    if (n > 0) lines.push(`son offre dans ${plural(n, "niveau", "niveaux")}`);
    lines.push("l'ossature prévue pour cette matière");
  }
  return lines;
};
